import { onMounted, onUnmounted, type Ref } from "vue";
import { isTopmostOverlay } from "./overlayStack";

type UseConfirmKeysOptions = {
    /** Корневой элемент подтверждения — по нему определяется, оно ли сверху. */
    content: Ref<HTMLElement | null>;
    /** Действие кнопки «Отмена». */
    cancel: () => void;
    /** Действие кнопки подтверждения. */
    confirm: () => void;
    /** Вернуть true, пока подтверждать нельзя (например, идёт запрос). */
    disabled?: () => boolean;
};

/**
 * Escape и Enter в дропдаунах подтверждения: Escape работает как «Отмена»,
 * Enter — как кнопка подтверждения.
 *
 * Слушатель висит на window в capture-фазе, чтобы отработать раньше
 * AppResizableDropdown (document, bubble) и AppSelect (document, capture):
 * иначе ESC закрыл бы дропдаун мимо обработчика @cancel, а внутри селекта
 * свернул бы заодно и сам селект.
 *
 * Подписка живёт от onMounted до onUnmounted — этого достаточно, потому что
 * содержимое дропдауна монтируется ровно на время, пока он открыт.
 */
export function useConfirmKeys({ content, cancel, confirm, disabled }: UseConfirmKeysOptions) {
    // Подтверждение отправляем один раз за жизнь компонента, то есть пока
    // дропдаун открыт.
    let confirmed = false;

    const handleKeydown = (e: KeyboardEvent) => {
        if (e.key !== "Escape" && e.key !== "Enter") {
            return;
        }
        if (e.ctrlKey || e.metaKey || e.altKey) {
            return;
        }
        if (!isTopmostOverlay(content.value)) {
            return;
        }

        if (e.key === "Escape") {
            e.preventDefault();
            e.stopImmediatePropagation();
            if (!e.repeat) {
                cancel();
            }
            return;
        }

        // Пользователь дошёл табом до конкретной кнопки — пусть Enter нажмёт
        // именно её, а не подтверждение.
        const active = document.activeElement;
        if (active instanceof HTMLButtonElement && content.value?.contains(active)) {
            return;
        }

        // Гасим Enter независимо от того, дойдёт ли дело до подтверждения:
        // preventDefault обязателен, потому что stopImmediatePropagation не
        // отменяет действие по умолчанию, и у сфокусированной кнопки-активатора
        // Enter повторно вызвал бы клик, переоткрыв дропдаун.
        e.preventDefault();
        e.stopImmediatePropagation();

        // Удержание клавиши и повторные нажатия до размонтирования не должны
        // отправлять запрос ещё раз: часть вызывающих закрывает дропдаун только
        // после await.
        if (e.repeat || confirmed || disabled?.()) {
            return;
        }

        confirmed = true;
        confirm();
    };

    onMounted(() => window.addEventListener("keydown", handleKeydown, true));
    onUnmounted(() => window.removeEventListener("keydown", handleKeydown, true));
}
